import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Gem, ArrowRight, Quote, Star } from 'lucide-react';
import { getTestimonials } from '../mock';
import { fetchProducts } from '../lib/api';
import ownerImg from '../assets/owner_new.jpeg';
import aboutStoryImg from '../assets/about_story.webp';
import PhotoTestimonials from '../components/PhotoTestimonials';
import Reveal from '../components/Reveal';

const WRITTEN_REVIEWS = [
  { name: 'Tierra M.', location: 'Atlanta, GA', tag: 'Premium Quality',
    text: 'I have ordered from Nikki\u2019s Next Level more times than I can count and the quality is consistent every single time. The bundles are thick from root to tip, barely shed, and last me install after install. Nothing else compares.' },
  { name: 'Danielle P.', location: 'Houston, TX', tag: 'Fast Delivery',
    text: 'My order arrived faster than I expected and was packaged so beautifully. I placed it on a Monday and had my hair by Wednesday \u2014 perfect timing before my trip. Delivery is always quick and reliable.' },
  { name: 'Keisha R.', location: 'Chicago, IL', tag: 'Custom Color',
    text: 'I requested a custom color for my bundles and Brooke got the shade absolutely perfect. The blend was flawless and looked exactly like the inspiration photo I sent. I felt like it was made just for me.' },
  { name: 'Monique S.', location: 'Charlotte, NC', tag: 'Amazing Service',
    text: 'I had a small issue with my order and spoke directly with Brooke. She was so warm and professional, and she fixed everything immediately \u2014 no stress at all. That kind of customer care keeps me loyal.' },
  { name: 'Alexis W.', location: 'Detroit, MI', tag: 'Returning Customer',
    text: 'I\u2019m a repeat customer for a reason. Every product I\u2019ve ordered \u2014 wigs, frontals, braiding hair \u2014 has been premium quality. This is my go-to and the only place I trust with my hair now.' },
  { name: 'Brittany J.', location: 'Dallas, TX', tag: 'Braiding Hair',
    text: 'The pre-stretched braiding hair is feather light and truly itch-free. It held my style for weeks and got me so many compliments. Speaking with Brooke was such a pleasant experience too \u2014 she really cares.' },
];

const About = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [collage, setCollage] = useState([]);
  useEffect(() => {
    fetchProducts()
      .then((all) => {
        const nonAcc = all.filter((p) => p.category !== 'accessories');
        // Pull six varied Bob & Pixie shade images so the collage shows short styles too
        const bob = all.find((p) => p.id === 'qc-blunt-bob');
        const pixieF = all.find((p) => p.id === 'qc-pixie-fashion');
        const pixieN = all.find((p) => p.id === 'qc-pixie-natural');
        const bobShades = bob?.color_images ? Object.values(bob.color_images) : [];
        const pixieShades = pixieF?.color_images ? Object.values(pixieF.color_images) : [];
        const sixBobPixie = [
          bobShades[0], bobShades[7], bobShades[13],
          pixieN?.image, pixieShades[2], pixieShades[7],
        ].filter(Boolean).slice(0, 6);

        const others = nonAcc.map((p) => p.image).filter(Boolean).filter((img) => !sixBobPixie.includes(img));
        // interleave: build 12 other looks and splice the six bob/pixie across the grid
        const merged = others.slice(0, 12);
        [1, 4, 7, 10, 13, 16].forEach((pos, i) => {
          if (sixBobPixie[i]) merged.splice(Math.min(pos, merged.length), 0, sixBobPixie[i]);
        });
        setCollage(merged.slice(0, 18));
      })
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-[#0b0b0b] py-28 sm:py-36 overflow-hidden">
        <img src={aboutStoryImg} alt="African American woman with 20 inch body wave hair, professionally dressed" className="absolute right-0 top-0 h-full w-1/2 object-cover object-top opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/70 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="max-w-xl">
            <p className="overline text-gold-gradient mb-4">Our Story</p>
            <h1 className="font-display text-5xl sm:text-7xl text-[#f7f3ec] leading-tight mb-6">Crafted for the woman who refuses to blend in.</h1>
            <p className="font-body text-[#cfc7b8] text-lg leading-relaxed">Nikki’s Next Level was born from a simple belief — that every woman deserves hair that makes her feel unstoppable.</p>
          </div>
        </div>
      </section>

      {/* PRODUCT COLLAGE HEADER — mosaic of the full collection (excludes accessories) */}
      {collage.length > 0 && (
        <section className="relative bg-[#0b0b0b] overflow-hidden" data-testid="about-collage-header">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-0.5">
            {collage.slice(0, 18).map((src, i) => (
              <div key={i} className="relative aspect-square overflow-hidden bg-[#141311]">
                <img src={src} alt="" loading="lazy"
                  className="w-full h-full object-cover opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-700" />
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/55 to-[#0b0b0b]/35" />
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <p className="overline text-gold-gradient mb-4">The Full Collection</p>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-[#f7f3ec] leading-tight max-w-4xl drop-shadow-2xl">
              Every texture. Every length. <span className="italic text-gold-gradient">One standard.</span>
            </h2>
            <Link to="/shop" className="btn-gold mt-8 px-9 py-4 pointer-events-auto"><span>Explore Everything</span></Link>
          </div>
        </section>
      )}
      <section className="max-w-[1000px] mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
        <Reveal>
        <Sparkles size={30} className="text-[#c6a15b] mx-auto mb-6" />
        <h2 className="font-display text-4xl sm:text-5xl text-[#1a1a1a] leading-tight mb-8">More than hair. A statement.</h2>
        <p className="font-body text-[#4a463d] text-lg leading-loose">
          What began as a passion for premium hair has grown into a destination for wigs, braiding hair and luxury bundles trusted by thousands of women nationwide. Every unit is hand-selected, quality-checked, and chosen because it meets one standard: the next level. We believe luxury isn’t about price — it’s about how you feel the moment you look in the mirror.
        </p>
        </Reveal>
      </section>

      {/* MEET THE OWNER */}
      <section className="relative bg-[#0b0b0b] overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(198,161,91,0.14),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Portrait */}
          <Reveal direction="left" className="relative mx-auto lg:mx-0 max-w-md w-full">
            <div className="absolute -inset-3 border border-[#c6a15b]/40 translate-x-4 translate-y-4" />
            <div className="relative overflow-hidden">
              <img src={ownerImg} alt="Brooke N. Knight, Founder of Nikki's Next Level" className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b]/40 to-transparent" />
            </div>
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 lg:left-8 lg:translate-x-0 bg-gold-sheen px-6 py-3 shadow-xl">
              <p className="font-serif-alt text-[#231a08] text-lg tracking-wide whitespace-nowrap">Brooke N. Knight</p>
              <p className="overline text-[#4a3410] text-[0.55rem] text-center lg:text-left">Founder &amp; CEO</p>
            </div>
          </Reveal>

          {/* Bio */}
          <Reveal direction="right" className="lg:pl-4 pt-8 lg:pt-0">
            <p className="overline text-gold-gradient mb-4">Meet The Owner</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec] leading-[1.05] mb-6">
              The visionary behind the <span className="italic text-gold-gradient">brand.</span>
            </h2>
            <p className="font-body text-[#cfc7b8] leading-loose mb-6">
              <span className="font-medium text-[#e7d3a1]">Nikki’s Next Level LLC.</span> is a beauty and hair company founded by Brooke N. Knight in 2017, driven by the desire to empower women through access to high-quality hair and beauty products.
            </p>
            <p className="font-body text-[#9c9587] leading-loose mb-8">
              Our brand offers premium virgin hair, affordable luxury options, and economy-friendly selections that cater to a diverse customer base — including stylists, salons, individuals with hair loss, and everyday beauty enthusiasts. We aim to provide premium products that enhance both confidence and appearance.
            </p>
            <div className="flex items-center gap-4 border-l-2 border-[#c6a15b] pl-5">
              <Quote size={26} className="text-[#c6a15b] shrink-0" />
              <p className="font-display text-2xl sm:text-3xl text-[#f7f3ec] italic">“Where Empowerment Meets Beauty.”</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#141311] py-20 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="overline text-gold-gradient mb-3">What We Stand For</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec]">Our Promise</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { icon: Gem, title: 'Uncompromising Quality', text: 'Only 100% virgin human hair and premium fibers. If it doesn’t meet our standard, it doesn’t make the shelf.' },
              { icon: Heart, title: 'Made For You', text: 'From glueless beginner-friendly units to raw bundles for pros — there’s a next level for everyone.' },
              { icon: Sparkles, title: 'Confidence First', text: 'We measure success by the way you feel walking out the door. Radiant, powerful, unmistakably you.' },
            ].map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12}>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-5 border border-[#c6a15b]/40 flex items-center justify-center">
                  <v.icon size={26} className="text-[#c6a15b]" />
                </div>
                <h3 className="font-display text-2xl text-[#f7f3ec] mb-3">{v.title}</h3>
                <p className="font-body text-sm text-[#9c9587] leading-relaxed max-w-xs mx-auto">{v.text}</p>
              </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Written reviews */}
      <section className="bg-[#f2ece0] py-20 sm:py-28 border-t border-[#e6ddcd]" data-testid="about-written-reviews">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="overline text-[#a8823c] mb-3">In Their Words</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#1a1a1a]">Why Queens Keep Coming Back</h2>
            <div className="gold-line w-24 mx-auto mt-6" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {WRITTEN_REVIEWS.map((r, i) => (
              <div key={i} data-testid={`written-review-${i}`}
                className="bg-white border border-[#e6ddcd] p-7 flex flex-col hover:border-[#c6a15b]/50 transition-colors">
                <Quote size={22} className="text-[#c6a15b] mb-4" />
                <p className="font-body text-[#4a463d] leading-relaxed flex-1">"{r.text}"</p>
                <div className="flex items-center gap-0.5 mt-5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={13} className="fill-[#c6a15b] text-[#c6a15b]" />
                  ))}
                </div>
                <p className="overline text-[0.6rem] text-[#6b6459] mt-3">{r.name} · {r.location}</p>
                <p className="font-body text-xs text-[#a8823c] mt-1">{r.tag}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-24 text-center">
        <h2 className="font-display text-4xl sm:text-6xl text-[#1a1a1a] mb-6">Ready to reach your next level?</h2>
        <Link to="/shop" className="btn-gold inline-flex items-center gap-2 px-10 py-4"><span className="flex items-center gap-2">Shop The Collection <ArrowRight size={16} /></span></Link>
      </section>
    </div>
  );
};

export default About;
